import { normalizeZuuFnaQuestionList } from "./zuuFnaFieldMap.js";

type QuestionList = Array<Record<string, any>>;
type FinancialLike = Record<string, any> | null | undefined;

function getQuestionValueFromAnalysis(
  list: QuestionList | undefined,
  questionKey: string,
  type: "RADIO" | "CHECKBOX" | "INPUT",
) {
  const question = list?.find((item) => item?.question === questionKey);
  if (!question) return undefined;

  if (type === "INPUT") {
    return question.items?.find((item: any) => item.key === "A")?.value;
  }

  const selectedKeys =
    question.items
      ?.filter((item: any) => item.value === true)
      .map((item: any) => item.key) || [];

  if (type === "CHECKBOX") {
    return selectedKeys;
  }

  return selectedKeys[0];
}

export function extractLegacyPremiumFinancing(
  premiumFinanceAnalysis: QuestionList | undefined,
) {
  const repaymentSource = getQuestionValueFromAnalysis(
    premiumFinanceAnalysis,
    "premiumFinancingRepaymentSource",
    "RADIO",
  );
  const estimateConsent = getQuestionValueFromAnalysis(
    premiumFinanceAnalysis,
    "willingToProvidePremiumFinancingInfo",
    "RADIO",
  );

  const premiumFinancingItem = {
    lenderName: getQuestionValueFromAnalysis(
      premiumFinanceAnalysis,
      "willingToProvidePremiumFinancingInfo_A_nameOfLender",
      "INPUT",
    ),
    loanAmount: getQuestionValueFromAnalysis(
      premiumFinanceAnalysis,
      "willingToProvidePremiumFinancingInfo_A_loanAmount",
      "INPUT",
    ),
    loanInterestRate: getQuestionValueFromAnalysis(
      premiumFinanceAnalysis,
      "willingToProvidePremiumFinancingInfo_A_loanInterestRate",
      "INPUT",
    ),
    loanTerm: getQuestionValueFromAnalysis(
      premiumFinanceAnalysis,
      "willingToProvidePremiumFinancingInfo_A_loanTenor",
      "INPUT",
    ),
    repaymentAmount: getQuestionValueFromAnalysis(
      premiumFinanceAnalysis,
      "willingToProvidePremiumFinancingInfo_A_eachInstallment",
      "INPUT",
    ),
  };

  const hasPremiumFinancingItem = Object.values(premiumFinancingItem).some((value) => {
    if (value === undefined || value === null) return false;
    return typeof value === "string" ? value.trim().length > 0 : true;
  });

  return {
    premiumFinancingRepaymentSources: repaymentSource ? [repaymentSource] : undefined,
    premiumFinancingRepaymentSourceOther: getQuestionValueFromAnalysis(
      premiumFinanceAnalysis,
      "premiumFinancingRepaymentSource_D_remark",
      "INPUT",
    ),
    outstandingMortgageLoanAmount: getQuestionValueFromAnalysis(
      premiumFinanceAnalysis,
      "outstandingMortgageAmount",
      "INPUT",
    ),
    outstandingOtherLoanAmount: getQuestionValueFromAnalysis(
      premiumFinanceAnalysis,
      "outstandingOtherLoansAmount",
      "INPUT",
    ),
    premiumFinancingEstimateConsent:
      estimateConsent === "A" ? true : estimateConsent === "B" ? false : undefined,
    premiumFinancing: hasPremiumFinancingItem ? [premiumFinancingItem] : undefined,
    premiumFinancingImportantDisclosureConfirmed:
      getQuestionValueFromAnalysis(
        premiumFinanceAnalysis,
        "confirmedImportantNotes",
        "RADIO",
      ) === "A",
    premiumFinancingRepaymentConfirmed:
      getQuestionValueFromAnalysis(
        premiumFinanceAnalysis,
        "hasSufficientFundsToRepayLoan",
        "RADIO",
      ) === "A",
  };
}

export function normalizeOrderFinancialForDisplay(financial: FinancialLike) {
  const premiumFinanceAnalysis = Array.isArray(financial?.premiumFinanceAnalysis)
    ? financial.premiumFinanceAnalysis
    : [];
  const legacyPremiumFinancing = extractLegacyPremiumFinancing(
    premiumFinanceAnalysis,
  );

  return {
    ...(financial || {}),
    financialAnalysis: normalizeZuuFnaQuestionList(
      Array.isArray(financial?.financialAnalysis) ? financial.financialAnalysis : [],
    ),
    suggestionsAndReasons: Array.isArray(financial?.suggestionsAndReasons)
      ? financial.suggestionsAndReasons
      : [],
    premiumFinanceAnalysis,
    premiumFinancingRepaymentSources:
      financial?.premiumFinancingRepaymentSources ??
      legacyPremiumFinancing.premiumFinancingRepaymentSources,
    premiumFinancingRepaymentSourceOther:
      financial?.premiumFinancingRepaymentSourceOther ??
      legacyPremiumFinancing.premiumFinancingRepaymentSourceOther,
    outstandingMortgageLoanAmount:
      financial?.outstandingMortgageLoanAmount ??
      legacyPremiumFinancing.outstandingMortgageLoanAmount,
    outstandingOtherLoanAmount:
      financial?.outstandingOtherLoanAmount ??
      legacyPremiumFinancing.outstandingOtherLoanAmount,
    premiumFinancingEstimateConsent:
      financial?.premiumFinancingEstimateConsent ??
      legacyPremiumFinancing.premiumFinancingEstimateConsent,
    premiumFinancing:
      financial?.premiumFinancing ?? legacyPremiumFinancing.premiumFinancing,
    premiumFinancingImportantDisclosureConfirmed:
      financial?.premiumFinancingImportantDisclosureConfirmed ??
      legacyPremiumFinancing.premiumFinancingImportantDisclosureConfirmed,
    premiumFinancingRepaymentConfirmed:
      financial?.premiumFinancingRepaymentConfirmed ??
      legacyPremiumFinancing.premiumFinancingRepaymentConfirmed,
  };
}
